import {useCallback} from 'react'
import {GearIcon} from '@primer/octicons-react'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {LabelPicker as ItemPickerLabel, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {PullRequestLabel} from '@github-ui/dotcom-schema/types/pull_requests/v1/labels'
import {useModifyLabels} from '../../mutations/use-modify-labels'
import {useLabels} from './data/use-labels'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'

const EMPTY_LABELS: PullRequestLabel[] = []

export function LabelsPicker() {
  const {data} = useLabels()
  const {owner, repo} = useRouteParams(pullRequestsLayoutRoute)
  const {mutate, isPending: isMutationPending} = useModifyLabels()
  const labels = data?.labels ?? EMPTY_LABELS

  const pickerLabels: LabelPickerLabel[] = labels.map(label => ({
    id: label.nodeId ?? '',
    name: label.name,
    nameHTML: label.name,
    color: label.color,
    description: label.description || null,
    url: `/${owner}/${repo}/labels/${encodeURIComponent(label.name)}`,
  }))

  const handleSelectionChange = useCallback(
    (nextLabels: LabelPickerLabel[]) => {
      const previousIds = new Set(labels.map(label => label.nodeId ?? ''))
      const nextIds = new Set(nextLabels.map(label => label.id))
      const addLabels = nextLabels.filter(label => !previousIds.has(label.id)).map(label => label.id)
      const removeLabels = labels.map(label => label.nodeId ?? '').filter(id => id && !nextIds.has(id))

      if (addLabels.length > 0 || removeLabels.length > 0) {
        mutate({addLabels, removeLabels, nextLabels})
      }
    },
    [labels, mutate],
  )

  if (!data) return null

  return (
    <ItemPickerLabel
      repo={repo}
      owner={owner}
      labels={pickerLabels}
      canCreateLabel={false}
      isLoading={isMutationPending}
      onSelectionChange={handleSelectionChange}
      anchorElement={(anchorProps, ref) => (
        <SidebarDisclosureHeader
          title="Labels"
          trailingIcon={GearIcon}
          ref={ref}
          aria-label="Manage labels"
          {...anchorProps}
        />
      )}
    />
  )
}
